import { type SubmitEvent, useState, useEffect, useEffectEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { AlertCircle, ArrowLeft, Loader2, KeyRound, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuthHook';
import { useVerifyMagicLink, useVerifyOTP } from '@/hooks/react-query/useAuth';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { safeReturnPath } from '@/utils/safe-return-path';

function VerifyPage() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const email = searchParams.get('email') ?? '';
  const token = searchParams.get('token') ?? '';
  const next = safeReturnPath(searchParams.get('next'));
  const [code, setCode] = useState('');
  const [manualOTPFallback, setManualOTPFallback] = useState(false);

  const {
    mutate: verifyToken,
    isPending: isTokenPending,
    isError: isTokenError,
    error: tokenError,
  } = useVerifyMagicLink();

  const {
    mutate: verifyOTP,
    isPending: isOTPPending,
    isError: isOTPError,
    error: otpError,
  } = useVerifyOTP();

  const toDashboard = useEffectEvent(() => {
    navigate(next ?? '/dashboard', { replace: true });
  });

  const toLogin = () => {
    navigate('/auth/login', { replace: true });
  };

  useEffect(() => {
    if (isAuthenticated && (!email || user?.email === email)) {
      toDashboard();
    }
  }, [isAuthenticated, email, user?.email]);

  const handleConfirmLogin = () => {
    if (!token) return;
    verifyToken(token);
  };

  const handleOTPSubmit = (e: SubmitEvent) => {
    e.preventDefault();
    if (code.length !== 6 || !email) return;
    verifyOTP({ email, code });
  };

  const tokenErrorMessage =
    tokenError instanceof Error
      ? tokenError.message
      : 'Login link is invalid or expired';

  const otpErrorMessage =
    otpError instanceof Error ? otpError.message : 'Failed to verify code';

  const showOTPFallback = manualOTPFallback || isTokenError;
  const showTokenConfirm = Boolean(token) && !showOTPFallback;

  // The confirm step is a lone button with no form to submit, so Enter needs
  // wiring; the OTP fallback below is a form and already submits on Enter.
  useKeyboardShortcuts(
    'verify-login',
    [
      {
        key: 'Enter',
        description: 'Confirm login',
        action: handleConfirmLogin,
        category: 'Authentication',
      },
    ],
    { enabled: showTokenConfirm && !isTokenPending }
  );

  if (!email) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-background dark:via-background dark:to-background flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-foreground mb-2">
              DFF Flow
            </h1>
            <p className="text-sm text-gray-600 dark:text-muted-foreground">
              Fund Administration Platform
            </p>
          </div>
        </div>
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white/80 dark:bg-card/80 backdrop-blur-sm py-8 px-4 shadow-xl dark:shadow-none border border-gray-100 dark:border-border sm:rounded-2xl sm:px-10 text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30">
                <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-foreground mb-2">
              Invalid link
            </h2>
            <p className="text-sm text-gray-600 dark:text-muted-foreground mb-6">
              This login link is invalid or missing required information.
            </p>
            <Button
              onClick={toLogin}
              className="w-full flex items-center justify-center"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Request new login link
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-background dark:via-background dark:to-background flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-foreground mb-2">
            DFF Flow
          </h1>
          <p className="text-sm text-gray-600 dark:text-muted-foreground">
            Fund Administration Platform
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white/80 dark:bg-card/80 backdrop-blur-sm py-8 px-4 shadow-xl dark:shadow-none border border-gray-100 dark:border-border sm:rounded-2xl sm:px-10">
          <div className="text-center">
            {showTokenConfirm ? (
              <>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-foreground mb-2">
                  Confirm your login
                </h2>
                <p className="text-sm text-gray-600 dark:text-muted-foreground mb-6">
                  Click below to sign in as{' '}
                  <span className="font-medium text-gray-900 dark:text-foreground">
                    {email}
                  </span>
                </p>

                <Button
                  onClick={handleConfirmLogin}
                  disabled={isTokenPending}
                  autoFocus
                  className="w-full flex items-center justify-center"
                >
                  {isTokenPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      <LogIn className="mr-2 h-4 w-4" />
                      Confirm Login
                    </>
                  )}
                </Button>

                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setManualOTPFallback(true)}
                    className="text-sm text-gray-500 dark:text-muted-foreground hover:text-gray-700 dark:hover:text-foreground underline"
                  >
                    Having trouble? Enter the 6-digit code from your email
                    instead.
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-foreground mb-2">
                  Enter your login code
                </h2>
                <p className="text-sm text-gray-600 dark:text-muted-foreground mb-6">
                  Enter the 6-digit code sent to{' '}
                  <span className="font-medium text-gray-900 dark:text-foreground">
                    {email}
                  </span>
                </p>

                {isTokenError && (
                  <Alert variant="destructive" className="mb-4 text-left">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{tokenErrorMessage}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleOTPSubmit} className="space-y-4">
                  <Input
                    value={code}
                    onChange={(e) =>
                      setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    placeholder="000000"
                    maxLength={6}
                    className="text-center text-2xl tracking-[0.3em] font-mono"
                    disabled={isOTPPending}
                    autoFocus
                  />

                  {isOTPError && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{otpErrorMessage}</AlertDescription>
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    disabled={code.length !== 6 || isOTPPending}
                    className="w-full flex items-center justify-center"
                  >
                    {isOTPPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <KeyRound className="mr-2 h-4 w-4" />
                        Verify code
                      </>
                    )}
                  </Button>
                </form>
              </>
            )}

            <div className="mt-4">
              <Button
                variant="ghost"
                onClick={toLogin}
                className="w-full flex items-center justify-center text-sm"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to login
              </Button>
            </div>
          </div>

          <div className="mt-6">
            <div className="text-center">
              <p className="text-xs text-gray-500 dark:text-muted-foreground">
                Having trouble? Contact your system administrator for
                assistance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifyPage;
